import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Box, Typography, Breadcrumbs, Link, Alert, LinearProgress, Skeleton } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import { trpc } from '../../lib/trpc';
import { MonitorForm, MonitorSubmitData } from '../../components/monitors/MonitorForm';

/**
 * Get plan name from limit value
 * -1 = unlimited (Enterprise), 5 = Free, 20 = Starter, 100 = Pro
 */
function getPlanName(limit: number): string {
  if (limit === -1) return 'Enterprise';
  if (limit <= 5) return 'Free';
  if (limit <= 20) return 'Starter';
  if (limit <= 100) return 'Pro';
  return 'Enterprise';
}

/**
 * Plan limit warning component
 */
function PlanLimitWarning({ current, limit }: { current: number; limit: number }) {
  // -1 means unlimited
  if (limit === -1) return null;

  const percentage = (current / limit) * 100;
  const isApproachingLimit = percentage >= 80;
  const isAtLimit = current >= limit;
  const planName = getPlanName(limit);

  if (!isApproachingLimit && !isAtLimit) {
    return null;
  }

  return (
    <Alert severity={isAtLimit ? 'error' : 'warning'} icon={<WarningIcon />} sx={{ mb: 3 }}>
      {isAtLimit ? (
        <>
          <strong>Monitor limit reached!</strong> You have used all {limit} monitors available on
          your {planName} plan.{' '}
          <Link component={RouterLink} to="/settings" underline="hover">
            Upgrade your plan
          </Link>{' '}
          to add more monitors.
        </>
      ) : (
        <>
          <strong>Approaching limit:</strong> You are using {current} of {limit} monitors on your{' '}
          {planName} plan ({percentage.toFixed(0)}%).{' '}
          <Link component={RouterLink} to="/settings" underline="hover">
            Consider upgrading
          </Link>{' '}
          to add more monitors.
        </>
      )}
      <Box sx={{ mt: 1 }}>
        <LinearProgress
          variant="determinate"
          value={Math.min(percentage, 100)}
          color={isAtLimit ? 'error' : 'warning'}
          sx={{ height: 6, borderRadius: 1 }}
        />
      </Box>
    </Alert>
  );
}

/**
 * Loading skeleton for plan limits
 */
function PlanLimitSkeleton() {
  return (
    <Box sx={{ mb: 3 }}>
      <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 1 }} />
    </Box>
  );
}

/**
 * Add monitor page component
 * Form to create a new monitor
 */
export function AddMonitorPage() {
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  // Fetch plan limits
  const limitsQuery = trpc.monitors.limits.useQuery();

  // Create monitor mutation
  const createMutation = trpc.monitors.create.useMutation({
    onSuccess: (data) => {
      // Invalidate queries to refresh data
      utils.monitors.list.invalidate();
      utils.monitors.limits.invalidate();

      // Navigate to the new monitor's detail page
      navigate(`/monitors/${data.id}`, {
        state: { message: 'Monitor created successfully!' },
      });
    },
  });

  const handleSubmit = (data: MonitorSubmitData) => {
    createMutation.mutate(data);
  };

  const handleCancel = () => {
    navigate('/monitors');
  };

  // Determine if user can create monitors (-1 means unlimited)
  // Default to false when data isn't available to prevent accidental creation
  const canCreate = limitsQuery.data
    ? limitsQuery.data.limit === -1 || limitsQuery.data.current < limitsQuery.data.limit
    : false;

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link component={RouterLink} to="/monitors" color="inherit" underline="hover">
          Monitors
        </Link>
        <Typography color="text.primary">Add Monitor</Typography>
      </Breadcrumbs>

      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        Add New Monitor
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Create a new monitor to track website uptime
      </Typography>

      {/* Plan Limit Warning */}
      {limitsQuery.isLoading ? (
        <PlanLimitSkeleton />
      ) : limitsQuery.isError ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          Unable to verify plan limits. Please try again later.
        </Alert>
      ) : limitsQuery.data ? (
        <PlanLimitWarning current={limitsQuery.data.current} limit={limitsQuery.data.limit} />
      ) : null}

      {/* Show form only if user can create monitors */}
      {canCreate ? (
        <MonitorForm
          mode="create"
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isLoading={createMutation.isLoading}
          error={createMutation.error?.message ?? null}
        />
      ) : (
        <Alert severity="error" sx={{ maxWidth: 800 }}>
          You have reached your monitor limit. Please upgrade your plan to add more monitors.
        </Alert>
      )}
    </Box>
  );
}

export default AddMonitorPage;
